import { memo } from 'react'
import { Factory, ChevronRight, Package } from 'lucide-react'
import { Card } from '../../../../../../components/ui/Card'
import { Badge } from '../../../../../../components/ui/Badge'
import { cn } from '../../../../../../lib/utils'

/**
 * Componente optimizado para mostrar tarjeta de línea de producción
 * Incluye información de la línea, orden activa y receta
 */
const LineCard = memo(({ line, onClick }) => {
  const activeOrder = line.activeOrder
  const progress = activeOrder && activeOrder.QuantityToProduce > 0
    ? Math.round((activeOrder.QuantityProduced / activeOrder.QuantityToProduce) * 100)
    : 0

  return (
    <Card
      className="cursor-pointer hover:border-blue-500 transition-all"
      onClick={onClick}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-4">
          <div className="flex items-center gap-3">
            <Factory className="w-8 h-8 text-blue-400" />
            <div>
              <h3 className="text-2xl font-bold text-white">{line.line}</h3>
              <p className="text-sm text-gray-400">{line.factory}</p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Métricas de la línea */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Métricas de Línea</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">OF activa:</span>
                <Badge
                  className={cn(
                    'text-xs',
                    activeOrder ? 'bg-green-600 text-white' : 'bg-amber-700/80 text-amber-100'
                  )}
                >
                  {activeOrder ? 'Con OF activa' : 'Sin OF activa'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">Órdenes:</span>
                <span className="text-sm text-white font-medium">{line.ordersCount || 0}</span>
              </div>
            </div>
          </div>

          {/* Orden Activa */}
          <div
            className={cn(
              'rounded-lg p-4 border',
              activeOrder
                ? 'border-green-600/60 bg-green-900/10'
                : 'border-amber-600/40 border-dashed bg-amber-900/5'
            )}
          >
            <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Orden Activa</h4>
            {activeOrder ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                  </span>
                  <p className="text-sm font-mono text-green-300 font-semibold">{activeOrder.OrderNumber}</p>
                </div>
                <p className="text-xs text-gray-300 truncate">{activeOrder.ProductName}</p>
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Progreso</span>
                    <span className="text-white font-medium">{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <Package className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-amber-400">Sin orden activa</p>
                <p className="text-xs text-gray-500 mt-1">
                  Entra en la línea e inicia una OF con Inicio OF
                </p>
              </div>
            )}
          </div>

          {/* Receta */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Receta</h4>
            {line.recipe && line.recipe.Components ? (
              <div className="space-y-2">
                <p className="text-sm font-mono text-purple-400">{line.recipe.SapCode}</p>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-300">{line.recipe.Components.length} componentes</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Sin receta cargada</p>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}, (prevProps, nextProps) => {
  const prevOrder = prevProps.line.activeOrder
  const nextOrder = nextProps.line.activeOrder
  const prevRecipe = prevProps.line.recipe
  const nextRecipe = nextProps.line.recipe

  return (
    prevProps.line.id === nextProps.line.id &&
    prevOrder?.OrderNumber === nextOrder?.OrderNumber &&
    prevOrder?.QuantityProduced === nextOrder?.QuantityProduced &&
    prevRecipe?.Components?.length === nextRecipe?.Components?.length
  )
})

LineCard.displayName = 'LineCard'

export default LineCard
